import { useMemo } from 'react';
import type { CashList } from '@pflegenavi/frontend/api-nursing-home';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import {
  CashListStorageType,
  FeatureFlag,
  FeatureFlagStatus,
} from '@pflegenavi/shared/api';

// Single source of truth for the Finance overview: hides the Pliant reference
// cash list (tiles, balance cards and the exported PDF) when the flag is on,
// and derives the cash/bank account splits from the filtered list.
export const useOverviewCashLists = (): {
  overviewCashLists: CashList[];
  cashAccounts: CashList[];
  bankAccounts: CashList[];
} => {
  const cashListConfiguration = useCashListConfiguration();

  const hidePliantReference =
    useFeatureFlag(FeatureFlag.HidePliantReferenceInOverview) ===
    FeatureFlagStatus.Enabled;

  const overviewCashLists = useMemo(() => {
    const all = cashListConfiguration?.cashLists ?? [];
    return hidePliantReference
      ? all.filter(
          (cashList) =>
            cashList.id !== cashListConfiguration.pliantReferenceCashListId
        )
      : all;
  }, [
    cashListConfiguration?.cashLists,
    cashListConfiguration.pliantReferenceCashListId,
    hidePliantReference,
  ]);

  const cashAccounts = useMemo(() => {
    return overviewCashLists.filter(
      (cashList) => cashList.storageType & CashListStorageType.Cash
    );
  }, [overviewCashLists]);

  const bankAccounts = useMemo(() => {
    return overviewCashLists.filter(
      (cashList) => cashList.storageType === CashListStorageType.BankAccount
    );
  }, [overviewCashLists]);

  return { overviewCashLists, cashAccounts, bankAccounts };
};
